import { useAuth } from "@babackapp/auth-client/hooks";
import useGetAlertingIssues from "@babackapp/private-api-client/client/use-get-alerting-issues";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@babackapp/ui/components/alert";
import { Button } from "@babackapp/ui/components/button";
import { TriangleAlert } from "lucide-react";
import { useState } from "react";

const ISSUE_LABEL_KEYS: Record<string, string> = {
  LABEL_FAILED: "settings.alerts.issues.types.LABEL_FAILED",
  REFUND_FAILED: "settings.alerts.issues.types.REFUND_FAILED",
  TREATMENT_FAILED: "settings.alerts.issues.types.TREATMENT_FAILED",
  LOGISTICS_REJECTED: "settings.alerts.issues.types.LOGISTICS_REJECTED",
  EXCHANGE_FAILED: "settings.alerts.issues.types.EXCHANGE_FAILED",
  OUT_OF_STOCK: "settings.alerts.issues.types.OUT_OF_STOCK",
  IMPORT_LINES_UNMATCHED: "settings.alerts.issues.types.IMPORT_LINES_UNMATCHED",
  IMPORT_FAILED: "settings.alerts.issues.types.IMPORT_FAILED",
  EXPORT_FAILED: "settings.alerts.issues.types.EXPORT_FAILED",
  MISSING_PACKAGING_DATA: "settings.alerts.issues.types.MISSING_PACKAGING_DATA",
};

type AlertingIssuesPanelProps = {
  shopId: string;
  /** Turns the alerts on and saves these digest recipients. */
  onEnable: (recipientEmails: string[]) => Promise<void>;
};

/**
 * The week had issues the daily email did not send, because the alerts are
 * off or nobody receives them: says which, and fixes it in one click. Alerts
 * on with no recipient are reported even on a quiet week (BBS-435).
 */
export function AlertingIssuesPanel({
  shopId,
  onEnable,
}: AlertingIssuesPanelProps) {
  const t = useTranslation();
  const { user } = useAuth();
  const { data } = useGetAlertingIssues({ shopId });
  const [isEnabling, setIsEnabling] = useState(false);

  if (!data) return null;
  const unreported = data.issues.filter(({ isReported }) => !isReported);
  // With the alerts on, only a missing recipient leaves an issue unreported.
  const { hasNoRecipients } = data;
  if (!unreported.length && !hasNoRecipients) return null;

  // Turned on with no recipient, nothing would be sent: the viewer gets them.
  const viewerEmail = user?.emails?.[0]?.address;
  const recipientEmails =
    data.recipientEmails.length > 0
      ? data.recipientEmails
      : viewerEmail
        ? [viewerEmail]
        : [];

  const enable = async () => {
    setIsEnabling(true);
    try {
      await onEnable(recipientEmails);
    } catch {
      // The mutation already shows the error toast.
    } finally {
      setIsEnabling(false);
    }
  };

  return (
    <Alert variant="warning" className="mb-6">
      <TriangleAlert className="h-4 w-4" />
      <AlertTitle>
        {unreported.length
          ? t("settings.alerts.issues.title")
          : t("settings.alerts.issues.noRecipients.title")}
      </AlertTitle>
      <AlertDescription className="space-y-3">
        <p>
          {hasNoRecipients
            ? t("settings.alerts.issues.noRecipients.description")
            : t("settings.alerts.issues.description")}
        </p>
        {unreported.length > 0 && (
          <div>
            <p className="text-xs font-medium uppercase text-muted-foreground">
              {t("settings.alerts.issues.window")}
            </p>
            <ul className="mt-1 space-y-0.5">
              {unreported.map((issue) => (
                <li key={issue.type} className="flex justify-between gap-4">
                  <span>
                    {ISSUE_LABEL_KEYS[issue.type]
                      ? t(ISSUE_LABEL_KEYS[issue.type])
                      : issue.type}
                  </span>
                  <span className="font-semibold tabular-nums">
                    {issue.count}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {recipientEmails.length > 0 && (
          <p className="text-sm">
            {t("settings.alerts.issues.recipientHint", {
              email: recipientEmails.join(", "),
            })}
          </p>
        )}
        <Button
          type="button"
          onClick={enable}
          disabled={isEnabling || recipientEmails.length === 0}
        >
          {hasNoRecipients
            ? t("settings.alerts.issues.noRecipients.enable")
            : t("settings.alerts.issues.enable")}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
